/* eslint-disable */
import { FormEvent, useEffect, useMemo, useState } from 'react';
import clx from 'classnames';
import { Content, Icon, Image, ListCell, ListView, Radio, RadioGroup, TextField, Typography } from '@modulor/react';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { paymentFlowSelector } from 'store/slices/global';
import { useNavigateWithSearch } from 'Modulor/hooks';
import { isDepositFlowType, isMethodAvailable } from 'utils';
import { UIPaymentGroups, UIPayoutGroups } from 'store/formatters';
import {
  currentGroupSelector,
  selectedCurrencySelector,
  setCurrentGroup,
  setPrevSelectedNetworkByCurrency,
} from 'store/slices/wallet';
import { internalNavigateBack } from 'utils/navigationControl';
import { useAppCore } from '../../../providers/AppCoreProvider/AppCoreContext';
import { CURRENCY_CHANGE_REASON } from '../../../providers/AppCoreProvider/AppCoreProvider';
import { MethodSubtitle } from '../../components/MethodCellContent/MethodSubtitle';
import { PaymentType } from '../types';
import { useSetHeaderConfig } from '../../components/Header/hooks/useSetHeaderConfig';

const MIN_OPTIONS_COUNT_FOR_SEARCH_FIELD = 15;

export const NetworkSelector = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigateWithSearch();
  const { translate, skeletonT } = useSkeletonTranslation();
  const flowType = useAppSelector(paymentFlowSelector);
  const { widgetData, handleCurrencyChange } = useAppCore();

  const isDeposit = isDepositFlowType(flowType);
  const flow = isDeposit ? 'deposit' : 'withdrawal';

  const selectedCurrency = useAppSelector(selectedCurrencySelector);
  const currentNetwork = useAppSelector(currentGroupSelector);
  const [searchValue, setSearchValue] = useState('');

  const { data } = widgetData;

  const groups = useMemo(() => {
    const methods = data?.methods || [];
    return [...methods].sort((a, b) => Number(isMethodAvailable(b)) - Number(isMethodAvailable(a)));
  }, [data?.methods]);

  const handleCloseWithoutSelection = () => {
    navigate(-1);
  };

  useSetHeaderConfig({
    preset: 'selectNetwork',
    subtitle: selectedCurrency?.alpha3,
    onNavigationAction: handleCloseWithoutSelection,
  });

  const displaySearchField = groups.length >= MIN_OPTIONS_COUNT_FOR_SEARCH_FIELD;

  const filteredNetworks = useMemo(
    () => groups.filter((network) => translate(network.displayName).toLowerCase().includes(searchValue.toLowerCase())),
    [groups, searchValue, translate],
  );

  useEffect(() => {
    setSearchValue('');
  }, [groups]);

  const handleSearch = (value: string | FormEvent<HTMLDivElement>) => {
    if (typeof value === 'string') {
      setSearchValue(value);
    }
  };

  const handleSelectNetwork = (network: UIPayoutGroups | UIPaymentGroups) => {
    const isChanged = currentNetwork?.id !== network.id;

    if (isChanged && selectedCurrency?.alpha3) {
      dispatch(setCurrentGroup(network));
      dispatch(setPrevSelectedNetworkByCurrency({ currency: selectedCurrency.alpha3, network }));
      handleCurrencyChange({
        currency: selectedCurrency,
        isCrypto: true,
        isOnRamp: false,
        currentGroup: network,
        tab: PaymentType.CRYPTO,
        reason: CURRENCY_CHANGE_REASON.CURRENCY_SELECT,
      });
      navigate(`/${flow}`, {
        replace: true,
      });
    } else {
      internalNavigateBack(navigate);
    }
  };

  return (
    <>
      {displaySearchField && (
        <div className="search-network">
          <TextField
            placeholder={translate('PH.CURRENCY.SEARCH.PLACEHOLDER')}
            type="text"
            value={searchValue}
            onChange={handleSearch}
            leftIcon="search"
          />
        </div>
      )}
      {!filteredNetworks.length && (
        <div className="network-selector-list empty-list-block">
          <Icon name="search" size={40} className="empty-list-block__icon" />
          <div className="empty-list-block__text">
            <Typography variant="title-2-semibold">{skeletonT('PH.LISTCELL.EMPTY.TITLE')}</Typography>
          </div>
        </div>
      )}
      <ListView key={searchValue} className="network-selector-list">
        <RadioGroup onChange={() => {}}>
          {filteredNetworks.map((network) => {
            const paymentNetwork = network as UIPaymentGroups;
            const isEnabled = isMethodAvailable(paymentNetwork);
            const cashbackInfo = isDeposit ? (
              <MethodSubtitle
                t={translate}
                processingTime={paymentNetwork.processingTimeTranslationKey}
                fee={paymentNetwork.merchantUserFee || paymentNetwork.fee || {}}
                lostProfitInfo={paymentNetwork.lostProfitInfo}
                isIncludeBonus={paymentNetwork.isIncludeBonus}
                limits={paymentNetwork.limits}
                currency={paymentNetwork.currencyAlpha3}
              />
            ) : (
              ''
            );
            return (
              <ListCell
                key={network.id}
                className={clx('network-selector-list-cell list-cell-control', {
                  'network-selector-list-cell__disabled': !isEnabled,
                })}
                text={translate(network.displayName || '')}
                icon={
                  <Image
                    src={network.logoUri}
                    alt={translate(network.displayName || '')}
                    decoding="async"
                    loading="lazy"
                  />
                }
                onClick={() => handleSelectNetwork(network)}
                content={
                  (
                    <Content>
                      <div className="network-cell-content">
                        {cashbackInfo}
                        <Radio value={String(network.id)} checked={network.id === currentNetwork?.id} />
                      </div>
                    </Content>
                  ) as unknown as string
                }
              />
            );
          })}
        </RadioGroup>
      </ListView>
    </>
  );
};
